import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import DashboardWrapper from '../Dashboard/components/DashboardWrapper'
import InnerBanner from '../../components/InnerBanner'
import Toast from '../../components/Toast/Toast'
import { PRIVATE_ROUTES } from '../../Routes/routes'
import { HUTRIT_PRIMARY_COLOR } from '../../utils/constants'
import { useGetProfileUserInfo } from '../../hooks/useGetProfileUserInfo'
import {
  ContractDocumentViewer,
  DEFAULT_VIEWER_HEIGHT,
} from '../../components/ContractDocumentViewer/ContractDocumentViewer'
import { TAGS } from '../../services/Tags'
import {
  getTalentContract,
  signTalentContract,
} from '../../services/NewServicesTalent'
import {
  getMissingTalentContractFields,
  getTalentContractData,
  normalizeTalentSignature,
} from '../../utils/talentContract'

const MySwal = withReactContent(Swal)

// ─── Contract status badge ────────────────────────────────────────────────────
const StatusBadge = ({ signed }) => (
  <span
    className="rounded-pill px-3 py-1"
    style={
      signed
        ? { background: '#dcfce7', color: '#166534', fontSize: 12, fontWeight: 700, border: '1px solid #bbf7d0' }
        : { background: '#fef9c3', color: '#92400e', fontSize: 12, fontWeight: 700, border: '1px solid #fde68a' }
    }
  >
    {signed ? 'Firmado' : 'Pendiente de firma'}
  </span>
)

// ─── Contract meta info bar ───────────────────────────────────────────────────
const ContractMetaBar = ({ contract }) => {
  if (!contract) return null
  const sig = contract.contract_signature ?? {}
  const status = contract.contract_status?.name ?? '—'
  const startDate = contract.start_date ? dayjs(contract.start_date).format('DD/MM/YYYY') : '—'
  const endDate = contract.end_date ? dayjs(contract.end_date).format('DD/MM/YYYY') : '—'
  const signedAt = sig.signed_at ? dayjs(sig.signed_at).format('DD/MM/YYYY') : null

  return (
    <div
      className="d-flex flex-wrap gap-3 mb-3 p-3 rounded"
      style={{ background: '#f9fafb', border: '1px solid #f3f4f6', fontSize: 12 }}
    >
      <div>
        <span style={{ color: '#9ca3af', marginRight: 4 }}>Estado:</span>
        <span style={{ fontWeight: 600, color: '#111827' }}>{status}</span>
      </div>
      <div>
        <span style={{ color: '#9ca3af', marginRight: 4 }}>Vigencia:</span>
        <span style={{ fontWeight: 600, color: '#111827' }}>{startDate} — {endDate}</span>
      </div>
      {signedAt && (
        <div>
          <span style={{ color: '#9ca3af', marginRight: 4 }}>Firmado:</span>
          <span style={{ fontWeight: 600, color: '#166534' }}>{signedAt}</span>
        </div>
      )}
    </div>
  )
}

// ─── Signature panel ──────────────────────────────────────────────────────────
const SignaturePanel = ({
  contracId,
  talentData,
  existingSignature,
  isAlreadySigned,
  finalPdfUrl,
  initialPdfUrl,
  onSigned,
  onSignedSuccess,
}) => {
  const [signature, setSignature] = useState('')
  const [accepted, setAccepted] = useState(isAlreadySigned)
  const [signed, setSigned] = useState(isAlreadySigned)

  useEffect(() => {
    setSignature(isAlreadySigned ? existingSignature : '')
    setAccepted(isAlreadySigned)
    setSigned(isAlreadySigned)
  }, [existingSignature, isAlreadySigned, contracId])

  const canSign = signature.trim().length >= 3 && accepted

  const today = new Date().toLocaleDateString('es-ES', {
    day: '2-digit', month: 'long', year: 'numeric',
  })

  const { mutate: signContractMutate, isLoading: isSigning } = useMutation(
    signTalentContract,
    {
      onSuccess: ({ response, res }) => {
        if (!response?.ok) {
          Toast({ icon: 'error', title: res?.msg || res?.error || 'No se pudo guardar la firma' })
          return
        }
        setSigned(true)
        Toast({ title: '¡Contrato firmado correctamente!', timer: 2000 })
        onSignedSuccess?.()
      },
      onError: () => {
        Toast({ icon: 'error', title: 'No se pudo guardar la firma' })
      },
    }
  )

  const handleSign = () => {
    if (!canSign) return
    if (!contracId) {
      Toast({ icon: 'error', title: 'No se encontró un contrato para firmar' })
      return
    }
    signContractMutate({
      signature: signature.trim(),
      accept: true,
      fullName: talentData.fullName,
      address: talentData.address,
      dni: talentData.dni,
      contracId: Number(contracId),
    })
  }

  if (signed) {
    const downloadUrl = finalPdfUrl || initialPdfUrl
    return (
      <div className="d-flex flex-column align-items-center gap-3 text-center py-2">
        <div
          className="d-flex align-items-center justify-content-center rounded-circle"
          style={{ width: 60, height: 60, background: '#dcfce7', border: '2px solid #86efac' }}
        >
          <svg width="26" height="26" fill="none" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <p style={{ fontWeight: 800, fontSize: 15, color: '#111827', marginBottom: 4 }}>¡Contrato firmado!</p>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 0 }}>Recibirás una copia por email.</p>
        </div>
        <div className="rounded p-3 w-100" style={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}>
          <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Firmado por</p>
          <p style={{ fontFamily: '"Dancing Script", cursive', fontSize: 28, color: '#1e40af', marginBottom: 2 }}>{signature}</p>
          <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 0 }}>{today}</p>
        </div>

        {downloadUrl && (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm w-100 d-flex align-items-center justify-content-center gap-2"
            style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', fontSize: 13, fontWeight: 600, borderRadius: 8, padding: 10, textDecoration: 'none' }}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Descargar contrato
          </a>
        )}

        <button
          type="button"
          onClick={() => onSigned && onSigned()}
          className="btn btn-bg-CTA-primary w-100"
          style={{ fontSize: 14 }}
        >
          Ir al dashboard
        </button>
      </div>
    )
  }

  return (
    <div className="d-flex flex-column gap-4">
      {/* Who signs */}
      <div>
        <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: 6 }}>
          Firmarás como
        </p>
        <div className="rounded p-3" style={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}>
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 32, height: 32, background: '#ede9fe', color: '#7c3aed', fontWeight: 800, fontSize: 13 }}
            >
              {(talentData.fullName || '?').charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="mb-0" style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{talentData.fullName}</p>
              <p className="mb-0" style={{ fontSize: 11, color: '#9ca3af' }}>DNI: {talentData.dni}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Full name from profile (pre-filled, read-only) */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', display: 'block', marginBottom: 8 }}>
          Nombre completo
        </label>
        <input
          type="text"
          className="form-control"
          value={talentData.fullName}
          readOnly
          style={{ fontSize: 15, background: '#f9fafb', color: '#374151' }}
        />
      </div>

      {/* Signature input (empty, user must type) */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', display: 'block', marginBottom: 8 }}>
          Firma <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className="form-control text-center"
          placeholder="Escribe tu firma"
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          style={{ fontSize: 15 }}
          autoComplete="off"
        />
      </div>

      {/* Live signature preview */}
      <div
        className="rounded d-flex align-items-center justify-content-center"
        style={{
          minHeight: 88,
          background: signature.trim() ? '#fafafa' : '#f9fafb',
          border: `2px dashed ${signature.trim() ? '#c7d2fe' : '#e5e7eb'}`,
          transition: 'border-color 0.2s',
        }}
      >
        {signature.trim() ? (
          <span style={{ fontFamily: '"Dancing Script", cursive', fontSize: 34, color: '#1e40af', userSelect: 'none' }}>
            {signature}
          </span>
        ) : (
          <span style={{ fontSize: 13, color: '#d1d5db', fontStyle: 'italic' }}>Tu firma aparecerá aquí</span>
        )}
      </div>

      {/* Date stamp */}
      <div className="d-flex align-items-center gap-2 px-3 py-2 rounded" style={{ background: '#f0f9ff', border: '1px solid #bae6fd' }}>
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#0284c7" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 600 }}>Fecha de firma: {today}</span>
      </div>

      {/* Checkbox */}
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id="acceptTermsTalent"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          style={{ cursor: 'pointer', width: 16, height: 16, marginTop: 2 }}
        />
        <label
          className="form-check-label"
          htmlFor="acceptTermsTalent"
          style={{ fontSize: 13, color: '#374151', cursor: 'pointer', paddingLeft: 6, lineHeight: 1.5 }}
        >
          He leído y acepto los <strong>términos y condiciones</strong> del contrato.
        </label>
      </div>

      {/* CTA */}
      <button
        type="button"
        onClick={handleSign}
        disabled={!canSign || isSigning}
        className="btn btn-bg-CTA-primary w-100"
        style={{ fontSize: 15, fontWeight: 700, padding: 12, opacity: canSign ? 1 : 0.45, transition: 'opacity 0.2s' }}
      >
        {isSigning ? 'Guardando firma...' : 'Firmar contrato'}
      </button>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────
const TalentContractSigning = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: profileUser = {}, isLoading: isLoadingProfile } = useGetProfileUserInfo()

  const { data: contractData, isLoading: isLoadingContract } = useQuery({
    queryKey: [TAGS.talent_contract],
    queryFn: getTalentContract,
  })

  const [localSigned, setLocalSigned] = useState(false)
  const missingModalShown = useRef(false)

  const contract = contractData?.res?.contract ?? null
  const sig = contract?.contract_signature ?? {}

  const talentData = getTalentContractData(profileUser)
  const missingFields = getMissingTalentContractFields(talentData)
  const isProfileIncomplete = missingFields.length > 0

  const existingSignature = normalizeTalentSignature(sig.references?.talentContactSignature)
  const isAlreadySigned = Boolean(existingSignature) || Boolean(sig.signed_at) || localSigned

  const handleDone = () => navigate(PRIVATE_ROUTES.dashboard)

  const handleSignedSuccess = () => {
    setLocalSigned(true)
    queryClient.invalidateQueries([TAGS.talent_contract])
  }

  // If the profile is missing required data, prompt the talent to complete it.
  useEffect(() => {
    if (isLoadingProfile || isLoadingContract) return
    if (!contract) return
    if (isAlreadySigned) return
    if (!isProfileIncomplete) return
    if (missingModalShown.current) return

    missingModalShown.current = true

    MySwal.fire({
      title: <strong style={{ fontSize: 20 }}>¡Casi listo para firmar! ✍️</strong>,
      html: (
        <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
          <p className="mb-2">
            Para poder firmar tu contrato necesitamos algunos datos personales que
            aún faltan en tu perfil:
          </p>
          <ul style={{ textAlign: 'left', display: 'inline-block', margin: '0 auto', color: '#b45309', fontWeight: 600 }}>
            {missingFields.map((field) => (
              <li key={field}>{field}</li>
            ))}
          </ul>
          <p className="mb-0 mt-3">
            Completa tu información personal y vuelve para firmar en segundos.
          </p>
        </div>
      ),
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Completar mi información',
      cancelButtonText: 'Más tarde',
      confirmButtonColor: HUTRIT_PRIMARY_COLOR,
      cancelButtonColor: '#9ca3af',
      allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        navigate(PRIVATE_ROUTES.talent_information)
      }
    })
  }, [
    isLoadingProfile,
    isLoadingContract,
    contract,
    isAlreadySigned,
    isProfileIncomplete,
  ])

  const loadingContent = (
    <div className="d-flex justify-content-center align-items-center py-5">
      <div className="spinner-border spinner-border-sm text-secondary" role="status" />
    </div>
  )

  if (isLoadingContract || isLoadingProfile) {
    return (
      <DashboardWrapper dashboardWrapperClassName="w-100">{loadingContent}</DashboardWrapper>
    )
  }

  if (!contract) {
    return (
      <DashboardWrapper dashboardWrapperClassName="w-100">
        <div className="p-3 p-lg-4" style={{ maxWidth: 700, margin: '0 auto' }}>
          <InnerBanner
            before="Inicio"
            beforeRoute={PRIVATE_ROUTES.dashboard}
            text="Mi contrato"
            className="mb-3"
          />
          <div
            className="rounded border p-4 text-center"
            style={{ borderColor: '#e5e7eb', background: '#f9fafb' }}
          >
            <p style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
              Aún no tienes un contrato disponible
            </p>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
              Cuando tengas un contrato asignado aparecerá aquí para que puedas revisarlo y firmarlo.
            </p>
            <button type="button" className="btn btn-bg-CTA-primary" onClick={handleDone}>
              Ir al dashboard
            </button>
          </div>
        </div>
      </DashboardWrapper>
    )
  }

  return (
    <DashboardWrapper dashboardWrapperClassName="w-100">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap');`}</style>

      <div className="p-3 p-lg-4" style={{ maxWidth: 1300, margin: '0 auto' }}>
        <InnerBanner
          before="Inicio"
          beforeRoute={PRIVATE_ROUTES.dashboard}
          text="Mi contrato"
          className="mb-3"
        />

        {/* Page heading */}
        <div className="d-flex align-items-center gap-2 mb-2">
          <h2 className="text-bg-brand mb-0" style={{ fontWeight: 800, fontSize: 22 }}>
            {isAlreadySigned ? 'Contrato firmado' : 'Revisar y firmar el contrato'}
          </h2>
          <StatusBadge signed={isAlreadySigned} />
        </div>
        <p className="mb-4" style={{ fontSize: 14, color: '#6b7280' }}>
          {isAlreadySigned
            ? 'Este contrato ya fue firmado. Puedes descargar una copia cuando lo necesites.'
            : 'Revisa el documento y firma tu contrato.'}
        </p>

        {/* Profile incomplete banner */}
        {!isAlreadySigned && isProfileIncomplete && (
          <div
            className="d-flex flex-wrap align-items-center gap-2 mb-4 p-3 rounded"
            style={{ background: '#fffbeb', border: '1px solid #fde68a' }}
          >
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#b45309" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span style={{ fontSize: 13, color: '#92400e', fontWeight: 600 }}>
              Faltan datos en tu perfil para poder firmar: {missingFields.join(', ')}.
            </span>
            <button
              type="button"
              className="btn btn-sm ms-auto"
              style={{ background: '#b45309', color: '#fff', fontSize: 13, fontWeight: 600, borderRadius: 8 }}
              onClick={() => navigate(PRIVATE_ROUTES.talent_information)}
            >
              Completar información
            </button>
          </div>
        )}

        {/* Contract metadata */}
        <ContractMetaBar contract={contract} />

        {/* Main two-column layout — viewer always visible */}
        <div className="row g-4 align-items-start">
          <div className="col-12 col-lg-7">
            <ContractDocumentViewer
              finalPdfUrl={sig.final_pdf_url}
              initialDocxUrl={sig.initial_pdf_url}
              height={DEFAULT_VIEWER_HEIGHT}
            />
          </div>

          <div className="col-12 col-lg-5">
            <div
              className="bg-white rounded border p-4"
              style={{
                borderColor: '#e5e7eb',
                position: isAlreadySigned ? 'static' : 'sticky',
                top: isAlreadySigned ? undefined : 24,
              }}
            >
              <p
                className="mb-3 pb-3"
                style={{ fontWeight: 700, fontSize: 15, color: '#111827', borderBottom: '1px solid #f3f4f6' }}
              >
                {isAlreadySigned ? 'Estado de la firma' : 'Bloque de firma digital'}
              </p>

              {!isAlreadySigned && isProfileIncomplete ? (
                <div className="d-flex flex-column align-items-center gap-3 text-center py-3">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle"
                    style={{ width: 56, height: 56, background: '#fef3c7', border: '2px solid #fde68a' }}
                  >
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#b45309" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <p style={{ fontSize: 14, color: '#374151', marginBottom: 0 }}>
                    Completa tu información personal para habilitar la firma del contrato.
                  </p>
                  <button
                    type="button"
                    className="btn btn-bg-CTA-primary w-100"
                    onClick={() => navigate(PRIVATE_ROUTES.talent_information)}
                  >
                    Completar mi información
                  </button>
                </div>
              ) : (
                <SignaturePanel
                  key={contract?.id}
                  contracId={sig.contract_id}
                  talentData={talentData}
                  existingSignature={existingSignature}
                  isAlreadySigned={isAlreadySigned}
                  finalPdfUrl={sig.final_pdf_url}
                  initialPdfUrl={sig.initial_pdf_url}
                  onSigned={handleDone}
                  onSignedSuccess={handleSignedSuccess}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardWrapper>
  )
}

export default TalentContractSigning
